import { graphql, useStaticQuery } from 'gatsby'
import { filter, get, isEmpty, map, some } from 'lodash'
import React, { useEffect, useState } from 'react'
import Carousel from 'react-multi-carousel'

import useHomepage from '../../hooks/useHomepage'
import useVisibility from '../../hooks/useVisibility'
import CarouselButtonGroup from '../common/CarouselButtonGroup'
import ScrollAnimation from '../scroll-animation/Container'
import Action from './Action'
import DeliveryTitle from './DeliveryTitle'
import NewDinnerPromoTile from './NewDinnerPromoTile'
import Title from './Title'
import { responsive } from './helpers'
import './meals.less'

type Slide = {
  image: { childImageSharp: { original: { src: string } } }
  logo: string
  logo_class: string
  discount: string
}

type Props = {
  isDelivery: boolean
}

const MealsContainer: React.FC<Props> = ({ isDelivery }) => {
  const { activeCityName, productOffering, animationProps, landingPageAlias } = useHomepage()
  const [slides, setSlides] = useState<Slide[]>([])
  const [isSectionInView, parentElement] = useVisibility<HTMLDivElement>(100, 350)
  const [showChild, setShowChild] = useState(false)
  const data = useStaticQuery(graphql`
    {
      allMealsJson {
        edges {
          node {
            image {
              childImageSharp {
                original {
                  src
                }
              }
            }
            logo
            logo_class
            city_name
            product_offering
            discount
          }
        }
      }
    }
  `)

  useEffect(() => {
    setShowChild(true)
  }, [])

  useEffect(() => {
    if (!isEmpty(activeCityName)) {
      const activeCityHasImages = some(
        data.allMealsJson.edges,
        (edge) => edge.node.city_name === activeCityName.toLowerCase(),
      )
      const city = activeCityHasImages ? activeCityName : 'new york city'
      const effectiveProductOffering = landingPageAlias === 'nydinner' ? 'lunch' : productOffering

      const edges = filter(data.allMealsJson.edges, (edge) => {
        const edgeProductOffering = get(edge, 'node.product_offering') || 'lunch'
        return (
          edge.node.image &&
          edge.node.city_name === city.toLowerCase() &&
          edgeProductOffering === effectiveProductOffering
        )
      })

      const slides = map(edges, (edge) => edge.node)
      setSlides(slides)
    }
  }, [activeCityName, productOffering])

  if (!showChild) {
    return <></>
  }
  return (
    <div className={`meals meals--${productOffering}`} ref={parentElement}>
      {isDelivery ? (
        <DeliveryTitle />
      ) : landingPageAlias === 'nydinner' ? (
        <NewDinnerPromoTile isSectionInView={isSectionInView} />
      ) : (
        <Title isSectionInView={isSectionInView} />
      )}

      <div className={`meals__wrapper ${!slides.length && 'meals__wrapper--empty'}`}>
        {slides.length && (
          <Carousel
            arrows={false}
            renderButtonGroupOutside={true}
            customButtonGroup={<CarouselButtonGroup />}
            className="meals__carousel"
            responsive={responsive}
            infinite={true}
            autoPlay={true}
            autoPlaySpeed={6000}
          >
            {slides.map((slide: Slide, index: number) => (
              <ScrollAnimation {...animationProps} key={index} delay={300 * index + 600}>
                <div className="meals__tile">
                  <div>
                    <div className="meals__image">
                      <img src={slide.image.childImageSharp.original.src} />
                      {slide.discount && <div className="meals__discount">save {slide.discount}</div>}
                    </div>
                    <div className={`meals__logo meals__logo--${slide.logo_class}`}>
                      <img src={slide.logo} />
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            ))}
          </Carousel>
        )}
      </div>
      <Action />
    </div>
  )
}

export default MealsContainer
